import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Dropdown from '../../Dropdown';
import Icon from '../Icon';
import {
    MENU_ALIGN,
    MENU_ALIGNS,
    MENU_ITEM_TONE,
    MENU_ITEM_TONES,
    MENU_SELECTED_ICON,
} from './constants';
import { normalizeMenuItems, toDataAttributes } from './helpers';

// Design System — Menu
// One list of options under a trigger. `items` is the list — `{ id, label, icon, description,
// selected, disabled, tone, dataAttributes, onSelect }` — and the chosen row carries a tick,
// so a menu that picks a value looks different from one that performs an action. The menu
// portals to document.body (the shared Dropdown), so no card, panel or table can clip it.
// `children` render after the items, for a footer row the list itself does not own.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const Menu = ({
    trigger,
    items = [],
    onSelect,
    align = MENU_ALIGN.LEFT,
    disabled = false,
    showSelectedIcon = true,
    className,
    menuClassName,
    itemClassName,
    children,
    ...rest
}) => (
    <Dropdown
        trigger={trigger}
        align={MENU_ALIGNS.includes(align) ? align : MENU_ALIGN.LEFT}
        disabled={disabled}
        className={classNames('ds-menu', className)}
        menuClassName={classNames('ds-menu__list', menuClassName)}
        {...rest}
    >
        {normalizeMenuItems(items).map((item) => (
            <Dropdown.Item
                key={item.key}
                className={classNames(
                    'ds-menu__item',
                    {
                        'ds-menu__item--selected': item.selected,
                        'ds-menu__item--danger': item.tone === MENU_ITEM_TONE.DANGER,
                        'is-selected': item.selected,
                    },
                    itemClassName,
                    item.className,
                )}
                disabled={item.disabled}
                onClick={() => {
                    if (item.disabled) return;
                    item.onSelect?.(item);
                    onSelect?.(item);
                }}
                dataAttributes={
                    item.dataAttributes
                        ? Object.entries(toDataAttributes(item.dataAttributes))
                        : undefined
                }
            >
                {item.icon && <Icon value={item.icon} noMargin className="ds-menu__item-icon" />}
                <span className="ds-menu__item-text">
                    <span className="ds-menu__item-label">{item.label}</span>
                    {item.description != null && item.description !== '' && (
                        <span className="ds-menu__item-description">{item.description}</span>
                    )}
                </span>
                {showSelectedIcon && item.selected && (
                    <Icon value={MENU_SELECTED_ICON} noMargin className="ds-menu__item-check" />
                )}
            </Dropdown.Item>
        ))}
        {children}
    </Dropdown>
);

Menu.propTypes = {
    /** The element that opens the menu */
    trigger: PropTypes.node.isRequired,
    /** The options, in order */
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.node,
            /** Secondary line under the label */
            description: PropTypes.node,
            /** Icon name (without the `icon-svg-` prefix) */
            icon: PropTypes.string,
            selected: PropTypes.bool,
            disabled: PropTypes.bool,
            /** `danger` colours a destructive option */
            tone: PropTypes.oneOf(MENU_ITEM_TONES),
            /** Extra data-* attributes, as an object or `[[name, value], …]` pairs */
            dataAttributes: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
            className: PropTypes.string,
            /** Called with the item; `onSelect` on the menu is called too */
            onSelect: PropTypes.func,
        }),
    ),
    /** Called with the chosen item */
    onSelect: PropTypes.func,
    /** Which edge the menu lines up with */
    align: PropTypes.oneOf(MENU_ALIGNS),
    /** Blocks opening */
    disabled: PropTypes.bool,
    /** Ticks the selected row; turn it off for a menu of actions */
    showSelectedIcon: PropTypes.bool,
    className: PropTypes.string,
    /** Extra class on the list, e.g. to widen it at one call site */
    menuClassName: PropTypes.string,
    /** Extra class on every item */
    itemClassName: PropTypes.string,
    /** Rendered after the items — a footer row, a custom control */
    children: PropTypes.node,
};

export { MENU_ALIGN, MENU_ALIGNS, MENU_ITEM_TONE, MENU_ITEM_TONES };
export { normalizeMenuItems, toDataAttributes };
export default Menu;
